import { categoryKinds, type Category, type CategoryKind, type IconAttribution, type User } from '@fin/shared';
import { useRef, useState } from 'react';
import { Button, ConfirmButton, Field, Icon, Input, Loading, Modal, PageHead, Section, Select, Tile, cx, toast, useAction } from '../../components/ui.tsx';
import { authStore, del, patch, post, setUser, signedOut, logout } from '../../lib/api.ts';
import { categoryTree, sortCats, useCategories, useContacts } from '../../lib/data.ts';
import { kindLabels } from '../../lib/format.ts';
import { categoryColor, categoryIcon, iconPaths } from '../../lib/icons.tsx';
import { invalidate, invalidateMoney, useQuery } from '../../lib/query.ts';
import { IconImagePicker } from '../../components/IconImagePicker.tsx';

export function SettingsPage() {
  return (
    <div className="page settings">
      <PageHead title="Настройки" />
      <div className="grid-2">
        <div className="col">
          <ProfileSection />
          <SessionsSection />
          <ContactsSection />
        </div>
        <div className="col">
          <CategoriesSection />
        </div>
      </div>
    </div>
  );
}

function ProfileSection() {
  const user = authStore.get().user as User;
  const [days, setDays] = useState(user.periodStartDays.join(', '));
  const [name, setName] = useState(user.name);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const { busy, run } = useAction();
  return (
    <Section title="Профиль">
      <form className="form" onSubmit={(e) => {
        e.preventDefault();
        const list = days.split(/[,\s]+/).map(Number).filter((n) => n >= 1 && n <= 28);
        void run(async () => {
          setUser(await patch<User>('/me', { name, periodStartDays: list }));
          invalidate('/periods', '/dashboard');
        }, 'Сохранено');
      }}>
        <Field label="Имя"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Дни зарплаты" hint="Через запятую, 1–28. С этих дней начинаются бюджетные периоды"><Input value={days} onChange={(e) => setDays(e.target.value)} /></Field>
        <Button type="submit" size="sm" busy={busy}>Сохранить</Button>
      </form>
      <form className="form form-sep" onSubmit={(e) => {
        e.preventDefault();
        void run(async () => { await post('/me/password', { currentPassword: cur, newPassword: next }); setCur(''); setNext(''); signedOut(); }, 'Пароль изменён. Войдите снова');
      }}>
        <Field label="Текущий пароль"><Input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></Field>
        <Field label="Новый пароль" hint="Не короче 8 символов"><Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} minLength={8} /></Field>
        <Button type="submit" size="sm" busy={busy} disabled={!cur || next.length < 8}>Сменить пароль</Button>
      </form>
    </Section>
  );
}

const tileColor = (c: Category) => categoryColor(c);

function CategoriesSection() {
  const q = useCategories();
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [editing, setEditing] = useState<number | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const { busy, run } = useAction();
  const ofKind = (q.data ?? []).filter((c) => c.kind === kind);
  const tree = categoryTree(ofKind);
  const parent = tree.find((n) => n.cat.id === parentId)?.cat;
  // «+» on a main category starts a subcategory inside it
  const addInside = (c: Category) => {
    setParentId(c.id);
    requestAnimationFrame(() => nameRef.current?.focus());
  };
  const editingCat = q.data?.find((c) => c.id === editing);
  const row = (c: Category, child: boolean) => (
    <li key={c.id} className={cx('cat', child && 'child')}>
      <Tile icon={categoryIcon(c)} color={tileColor(c)} size="sm" image={c.iconImage} />
      <span className={c.archived ? 'muted' : ''}>
        {c.name}
        {c.investment ? <span className="badge">инвестиции</span> : null}
        {c.archived ? <span className="badge badge-muted">скрыта</span> : null}
      </span>
      <span className="cat-actions">
        {!child ? <button type="button" className="icon-btn" aria-label={`Подкатегория в «${c.name}»`} onClick={() => addInside(c)}><Icon name="plus" size={16} /></button> : null}
        <button type="button" className="icon-btn" aria-label={`Изменить ${c.name}`} onClick={() => setEditing(c.id)}><Icon name="edit" size={16} /></button>
      </span>
    </li>
  );
  return (
    <Section title="Категории">
      <Select aria-label="Тип категорий" value={kind} onChange={(e) => { setKind(e.target.value as CategoryKind); setParentId(''); }}>
        {categoryKinds.map((k) => <option key={k} value={k}>{kindLabels[k]}</option>)}
      </Select>
      {/* adding comes first: a long list must not push it off the screen */}
      <form className="inline-form" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        void run(async () => {
          await post('/categories', { name: name.trim(), kind, parentId: parentId || null });
          setName(''); invalidate('/categories');
        }, parentId ? 'Подкатегория добавлена' : 'Категория добавлена');
      }}>
        <Input ref={nameRef} placeholder={parent ? `Подкатегория в «${parent.name}»` : 'Новая категория'} value={name} onChange={(e) => setName(e.target.value)} aria-label={parent ? `Новая подкатегория в «${parent.name}»` : 'Новая категория'} />
        <Select aria-label="Внутри категории" value={parentId} onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Отдельная категория</option>
          {tree.map((n) => <option key={n.cat.id} value={n.cat.id}>внутри «{n.cat.name}»</option>)}
        </Select>
        <Button type="submit" size="sm" busy={busy}><Icon name="plus" size={16} /> Добавить</Button>
      </form>
      {!q.data ? <Loading /> : (
        <div className="cats">
          {tree.map((n) => (
            <ul key={n.cat.id} className={cx('cat-group', n.children.length > 0 && 'has-children')}>
              {row(n.cat, false)}
              {n.children.map((c) => row(c, true))}
            </ul>
          ))}
        </div>
      )}
      {editingCat && q.data ? <CategoryEditor key={editingCat.id} c={editingCat} all={q.data} onDone={() => setEditing(null)} /> : null}
    </Section>
  );
}

const iconChoices = ['tag', 'cart', 'burger', 'coffee', 'utensils', 'bag', 'shirt', 'laptop', 'repeat', 'car', 'bus', 'home', 'phone', 'wifi', 'gamepad', 'gift', 'heart', 'hand',
  'percent', 'health', 'scissors', 'book', 'plane', 'users', 'trend', 'briefcase', 'star', 'coins', 'tv', 'music', 'paw', 'drop', 'bolt', 'dumbbell', 'baby', 'tools', 'building', 'sparkle'];

function CategoryEditor({ c, all, onDone }: { c: Category; all: Category[]; onDone: () => void }) {
  const [name, setName] = useState(c.name);
  const [color, setColor] = useState(categoryColor(c));
  const [kind, setKind] = useState<CategoryKind>(c.kind);
  const [icon, setIcon] = useState(c.icon === 'tag' ? categoryIcon(c) : c.icon);
  const [image, setImage] = useState<string | null>(c.iconImage ?? null);
  const [attribution, setAttribution] = useState<IconAttribution | null>(c.iconAttribution ?? null);
  const [imageBusy, setImageBusy] = useState(false);
  const [parentId, setParentId] = useState<number | ''>(c.parentId ?? '');
  const [archived, setArchived] = useState(c.archived);
  const [investment, setInvestment] = useState(c.investment);
  const [into, setInto] = useState<number | ''>('');
  const { busy, run } = useAction();
  const hasChildren = all.some((x) => x.parentId === c.id);
  const parents = sortCats(all.filter((x) => x.id !== c.id && x.kind === kind && !x.parentId));
  const save = () => run(async () => {
    if (!name.trim()) throw new Error('Введите название');
    await patch(`/categories/${c.id}`, { name: name.trim(), color, kind, icon, iconImage: image, iconAttribution: attribution, parentId: parentId || null, archived, investment });
    invalidate('/categories'); invalidateMoney(); invalidate('/finplan'); onDone();
  }, 'Сохранено');
  return (
    <Modal className="modal-form" title={c.name} onClose={onDone} footer={<>
      <ConfirmButton size="md" onConfirm={async () => {
        try { await del(`/categories/${c.id}`); invalidate('/categories'); onDone(); }
        catch (e) { toast(`${(e as Error).message}. Объедините категорию с другой.`, 'error'); }
      }}>Удалить</ConfirmButton>
      <div className="row-gap"><Button onClick={onDone}>Отмена</Button><Button variant="primary" busy={busy} disabled={imageBusy} onClick={() => void save()}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="cat-edit-head">
          <Tile icon={icon} color={color} image={image} />
          <Field label="Название" className="grow"><Input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
        </div>
        <div className="form-row">
          <Field label="Тип" className="grow" hint={parentId ? 'Тип подкатегории — как у родительской' : undefined}>
            <Select value={kind} onChange={(e) => { setKind(e.target.value as CategoryKind); setParentId(''); }} disabled={!!parentId}>
              {categoryKinds.map((k) => <option key={k} value={k}>{kindLabels[k]}</option>)}
            </Select>
          </Field>
          <Field label="Цвет"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="color-input" /></Field>
        </div>
        {!hasChildren ? (
          <Field label="Расположение">
            <Select value={parentId} onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Отдельная категория</option>
              {parents.map((x) => <option key={x.id} value={x.id}>внутри «{x.name}»</option>)}
            </Select>
          </Field>
        ) : <p className="muted small">У категории есть подкатегории, поэтому она не может стать подкатегорией сама.</p>}
        <div className="field">
          <span className="field-label">Картинка</span>
          <IconImagePicker value={image} attribution={attribution} color={color} name={name} onBusyChange={setImageBusy}
            onChange={(picture, credits) => { setImage(picture); setAttribution(credits); }} />
        </div>
        <div className="field">
          <span className="field-label">Или иконка</span>
          <div className="icon-grid" role="radiogroup" aria-label="Иконка">
            {iconChoices.filter((i) => i in iconPaths).map((i) => (
              <button key={i} type="button" className={cx(i === icon && !image && 'on')} role="radio" aria-checked={i === icon && !image} aria-label={i} disabled={imageBusy}
                onClick={() => { setIcon(i); setImage(null); setAttribution(null); }}><Icon name={i} size={18} /></button>
            ))}
          </div>
        </div>
        <label className="check"><input type="checkbox" checked={investment} onChange={(e) => setInvestment(e.target.checked)} /> Считать инвестициями{kind === 'income' ? ' (вывод средств)' : ''}</label>
        <label className="check"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Скрыть из выбора</label>
        <div className="field form-sep">
          <span className="field-label">Объединить с другой категорией</span>
          <div className="inline-form">
            <Select value={into} onChange={(e) => setInto(e.target.value ? Number(e.target.value) : '')} aria-label="Объединить с">
              <option value="">Выберите категорию…</option>
              {sortCats(all.filter((x) => x.id !== c.id && x.kind === c.kind)).map((x) => <option key={x.id} value={x.id}>{x.parentId ? `${all.find((p) => p.id === x.parentId)?.name} · ` : ''}{x.name}</option>)}
            </Select>
            <Button size="sm" disabled={!into} busy={busy} onClick={() => run(async () => {
              await post(`/categories/${c.id}/merge`, { into }); invalidate('/categories'); invalidateMoney(); invalidate('/finplan'); onDone();
            }, 'Категории объединены')}>Объединить</Button>
          </div>
          <span className="field-hint">Операции перейдут в выбранную категорию, эта будет удалена</span>
        </div>
      </form>
    </Modal>
  );
}

function ContactsSection() {
  const q = useContacts();
  const [name, setName] = useState('');
  const { busy, run } = useAction();
  return (
    <Section title="Контакты" aside={<span className="muted small">Кредиторы и должники</span>}>
      {!q.data ? <Loading /> : (
        <ul className="ledger">
          {q.data.map((c) => (
            <li key={c.id} className="ledger-row">
              <span className="ledger-main">{c.name}</span>
              <span className="row-gap">
                {(c.balance ?? []).map((b) => (
                  <span key={b.currency} className="small">
                    {b.iOwe > 0 ? <span className="neg">должен {Math.round(b.iOwe).toLocaleString('ru-RU')} </span> : null}
                    {b.owedToMe > 0 ? <span className="pos">мне {Math.round(b.owedToMe).toLocaleString('ru-RU')} </span> : null}
                  </span>
                ))}
                <ConfirmButton onConfirm={async () => { await del(`/contacts/${c.id}`); invalidate('/contacts'); }}>Удалить</ConfirmButton>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="inline-form" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        void run(async () => { await post('/contacts', { name: name.trim() }); setName(''); invalidate('/contacts'); }, 'Контакт добавлен');
      }}>
        <Input placeholder="Имя" value={name} onChange={(e) => setName(e.target.value)} aria-label="Имя контакта" />
        <Button type="submit" size="sm" busy={busy}><Icon name="plus" size={16} /> Добавить</Button>
      </form>
    </Section>
  );
}

function SessionsSection() {
  const sessions = useQuery<{ id: string; device: string; current: boolean }[]>('/me/sessions');
  const { busy, run } = useAction();
  return <Section title="Устройства">
    {sessions.data?.map((session) => <div className="row" key={session.id}>
      <span>{session.device || 'Устройство'}{session.current ? ' · текущее' : ''}</span>
      <Button size="sm" variant="ghost" busy={busy} onClick={() => void run(async () => {
        if (session.current) await logout();
        else { await del('/me/sessions/' + session.id); invalidate('/me/sessions'); }
      })}>Завершить сессию</Button>
    </div>)}
    {sessions.error ? <p className="neg small">{sessions.error.message}</p> : null}
  </Section>;
}
